import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';

import { fetchOrgSettings, patchOrgSettings } from '../../../api/org-settings.js';
import { ApiError } from '../../../lib/api-client.js';

export function DisplaySettingsPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: ['org-settings'], queryFn: fetchOrgSettings });
  const [showColumnHints, setShowColumnHints] = useState(true);

  useEffect(() => {
    const display = settingsQuery.data?.settings.display;
    if (display) setShowColumnHints(display.showColumnHints);
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => patchOrgSettings({ display: { showColumnHints } }),
    onSuccess: () => {
      toast.success('Display settings saved');
      void queryClient.invalidateQueries({ queryKey: ['org-settings'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Could not save settings'),
  });

  if (settingsQuery.isLoading) return <LoadingState rows={4} />;
  if (settingsQuery.isError) return <ErrorState description="Could not load settings." />;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Display</h2>
        <p className="text-sm text-fg-secondary">
          Explanations for people who are still learning the tables.
        </p>
      </div>
      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          saveMutation.mutate();
        }}
        className="space-y-4"
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Column explanations</CardTitle>
          </CardHeader>
          <CardContent>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={showColumnHints}
                onChange={(event) => setShowColumnHints(event.target.checked)}
              />
              <span>
                <span className="font-medium">Show column explanations. </span>
                Hover a table heading to see what that column means. Turn this off once your team
                knows the tables.
              </span>
            </label>
          </CardContent>
        </Card>
        <Button type="submit" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving…' : 'Save settings'}
        </Button>
      </form>
    </div>
  );
}
